import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Button from '../components/Button';

const PageContainer = styled(motion.div)`
  max-width: 1100px;
  margin: 80px auto 2rem;
  padding: 0 1rem;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (min-width: 768px) {
    margin-top: 120px;
    padding: 0 2rem;
  }
`;

const FormCard = styled.div`
  background: white;
  padding: 1.2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 768px) { padding: 3rem; }
`;

const SectionTitle = styled.h3`
  color: #1e3a8a;
  margin: 2rem 0 1rem;
  border-bottom: 2px solid #f1f5f9;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
  box-sizing: border-box;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  box-sizing: border-box;

  label { font-weight: 600; font-size: 0.85rem; color: #1e3a8a; }
  .helper-text { font-size: 0.75rem; color: #64748b; margin-bottom: 0.2rem; }
  
  input, select, textarea {
    width: 100%;
    max-width: 100%;
    padding: 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 1rem;
    box-sizing: border-box;
    display: block;
    &:focus { outline: none; border-color: #1e3a8a; }
  }
`;

const DocList = styled.div`
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-sizing: border-box;
  ul { margin: 0.5rem 0; padding-left: 1.2rem; color: #334155; }
  li { margin-bottom: 0.4rem; font-size: 0.85rem; }
`;

const WhatsAppIcon = () => (
  <svg 
    viewBox="0 0 448 512" 
    width="18" 
    height="18" 
    fill="currentColor" 
    style={{ marginRight: '8px', verticalAlign: 'middle' }}
  >
    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-5.5-2.8-23.2-8.5-44.2-27.2-16.4-14.6-27.4-32.7-30.6-38.2-3.2-5.6-.3-8.6 2.5-11.3 2.5-2.5 5.5-6.5 8.3-9.7 2.8-3.3 3.7-5.6 5.5-9.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 13.2 5.8 23.5 9.2 31.6 11.8 13.3 4.2 25.4 3.6 35 2.2 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
  </svg>
);

const WorkWithUs = () => {
  const [formData, setFormData] = useState({
    position: '', first_name: '', surname: '', ni_number: '', phone: '',
    email: '', share_code: '', passport_expiry: '', dbs_number: '', dbs_expiry: '', references: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Employment Application - L&J Agape', 14, 22);
    autoTable(doc, {
      startY: 30,
      head: [['Section', 'Details']],
      body: [
        ['Position', data.position || 'N/A'],
        ['Name', `${data.first_name} ${data.surname}`.trim() || 'N/A'],
        ['N.I. Number', data.ni_number || 'N/A'],
        ['Phone', data.phone || 'N/A'],
        ['Email', data.email || 'N/A'],
        ['RTW Share Code', data.share_code || 'N/A'],
        ['Passport Expiry', data.passport_expiry || 'N/A'],
        ['DBS Number', data.dbs_number || 'N/A'],
        ['DBS Expiry', data.dbs_expiry || 'N/A'],
        ['References', data.references || 'N/A'],
        ['Date', new Date().toLocaleDateString()],
      ],
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 138] },
    });
    doc.save(`Application_${data.first_name || 'User'}.pdf`);
  };

  const sendWhatsApp = () => {
    const phone = "447907666278";
    const message = `*New Application Alert: L&J Agape*\n\n*PERSONAL DETAILS*\n*Name:* ${formData.first_name} ${formData.surname}\n*Position:* ${formData.position}\n*NI Number:* ${formData.ni_number}\n*Phone:* ${formData.phone}\n*Email:* ${formData.email}\n\n*LEGAL & RTW*\n*Share Code:* ${formData.share_code}\n*Passport Valid Until:* ${formData.passport_expiry}\n*DBS Number:* ${formData.dbs_number}\n*DBS Valid Until:* ${formData.dbs_expiry}\n\n*REFERENCES*\n${formData.references || 'Pending'}\n\n*Note:* I am now sending the required documents via email to HR.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const sendEmail = () => {
    const email = "enquiries@ljagapehc.co.uk";
    const subject = `Application Documents - ${formData.first_name} ${formData.surname}`;
    const body = `Hello HR Team,\n\nI have just submitted my application details via the Alert HR system. Please find attached the following required documents:\n\n1. Updated CV\n2. Passport / Photo ID\n3. DBS Certificate\n4. Proof of Address\n5. Completed L&J Application Form\n\nBest regards,\n${formData.first_name} ${formData.surname}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <PageContainer initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <FormCard>
        <h1 style={{ textAlign: 'center', color: '#1e3a8a', fontSize: '1.5rem' }}>Employment Application</h1>
        <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '2rem', fontSize: '0.85rem' }}>Fill details, Alert HR via WhatsApp, then Email Documents.</p>

        <StyledForm>
          <SectionTitle>1. Position & Personal Details</SectionTitle>
          <Grid>
            <FormGroup>
              <label>Position Applied For*</label>
              <input type="text" name="position" required onChange={handleInputChange} placeholder="e.g. Care Professional" />
            </FormGroup>
            <FormGroup>
              <label>First Name*</label>
              <input type="text" name="first_name" placeholder="First Name" required onChange={handleInputChange} />
            </FormGroup>
            <FormGroup>
              <label>Surname*</label>
              <input type="text" name="surname" placeholder="Surname" required onChange={handleInputChange} />
            </FormGroup>
          </Grid>
          <Grid>
            <FormGroup><label>N.I. Number*</label><input type="text" name="ni_number" required onChange={handleInputChange} /></FormGroup>
            <FormGroup><label>Mobile Number*</label><input type="tel" name="phone" required onChange={handleInputChange} /></FormGroup>
          </Grid>
          <FormGroup><label>Email Address*</label><input type="email" name="email" required onChange={handleInputChange} /></FormGroup>

          <SectionTitle>2. Right to Work & Requirements</SectionTitle>
          <Grid>
            <FormGroup><label>RTW Share Code*</label><input type="text" name="share_code" required onChange={handleInputChange} /></FormGroup>
            <FormGroup><label>Passport Valid Until*</label><input type="date" name="passport_expiry" required onChange={handleInputChange} /></FormGroup>
          </Grid>
          <Grid>
            <FormGroup><label>DBS Certificate No.*</label><input type="text" name="dbs_number" required onChange={handleInputChange} /></FormGroup>
            <FormGroup><label>DBS Valid Until*</label><input type="date" name="dbs_expiry" required onChange={handleInputChange} /></FormGroup>
          </Grid>

          <SectionTitle>3. References</SectionTitle>
          <FormGroup>
            <label>Reference Details*</label>
            <p className="helper-text">Provide: Name, Status, Address, and Phone for 2 employers or 5 character contacts.</p>
            <textarea name="references" rows="5" placeholder="Details here..." onChange={handleInputChange} required></textarea>
          </FormGroup>

          <SectionTitle>4. Required Documentation</SectionTitle>
          <DocList>
            <p style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.9rem' }}>Email these to enquiries@ljagapehc.co.uk:</p>
            <ul>
              <li>CV, ID, DBS, Proof of Address, App Form</li>
            </ul>
          </DocList>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', marginTop: '1.5rem' }}>
            <Button type="button" onClick={() => generatePDF(formData)} color="primary" style={{ width: '100%' }}>Download PDF</Button>
            <Button type="button" onClick={sendWhatsApp} color="secondary" style={{ width: '100%', backgroundColor: '#25D366' }}>
              <WhatsAppIcon /> Alert HR
            </Button>
            <Button type="button" onClick={sendEmail} style={{ width: '100%', backgroundColor: '#1e3a8a', color: 'white' }}>Email Documents</Button>
          </div>
        </StyledForm>
      </FormCard>
    </PageContainer>
  );
};

export default WorkWithUs;